import { useT } from "../../../shared/i18n";
import { Banner, Card, Table } from "../../../shared/ui";
import { formatUsd } from "../observability/format";
import { KeyValueList } from "../KeyValueList";
import { formatDateTime } from "./format";
import type { UserBilling } from "./usersApi";

function BillingBody({ billing }: { billing: UserBilling }) {
  const t = useT();
  const { subscription } = billing;
  const none = t("admin.users.none");
  const noSubscription = t("admin.users.billing.noSubscription");
  const quota = Object.entries(billing.usage_this_hour.used_by_operation)
    .map(([operation, used]) => `${operation}: ${String(used)}`)
    .join(", ");
  const remaining = t("admin.users.billing.packRemaining");
  const packs = billing.usage_packs
    .map(
      (pack) =>
        `${pack.tier}: ${String(pack.remaining_credits)}/${String(pack.total_credits)} ${remaining}`,
    )
    .join(", ");
  const overrides = billing.entitlement_overrides
    .map((item) => item.capability)
    .join(", ");
  const period = subscription
    ? `${subscription.state}, ${t("admin.users.billing.period")}: ${formatDateTime(subscription.period_start)} - ${formatDateTime(subscription.period_end)}`
    : noSubscription;
  const cost = `${formatUsd(billing.model_cost_30d.cost_micros)} (${String(billing.model_cost_30d.calls)} ${t("admin.users.billing.calls")})`;
  return (
    <div className="flex flex-col gap-3">
      <KeyValueList
        items={[
          {
            label: t("admin.users.billing.plan"),
            value: subscription?.plan_id ?? noSubscription,
          },
          { label: t("admin.users.billing.subscription"), value: period },
          {
            label: t("admin.users.billing.quota"),
            value: quota || t("admin.users.billing.noQuota"),
          },
          { label: t("admin.users.billing.packs"), value: packs || none },
          {
            label: t("admin.users.billing.overrides"),
            value: overrides || none,
          },
          { label: t("admin.users.billing.cost"), value: cost },
        ]}
      />
      <Table
        caption={t("admin.users.billing.ordersCaption")}
        rows={billing.orders}
        rowKey={(row) => row.id}
        empty={
          <p className="text-body text-fg-muted">
            {t("admin.users.billing.noOrders")}
          </p>
        }
        columns={[
          {
            id: "order",
            header: t("admin.users.billing.col.order"),
            cell: (row) => (
              <span className="font-mono text-footnote">{row.id}</span>
            ),
          },
          {
            id: "kind",
            header: t("admin.users.billing.col.kind"),
            cell: (row) => row.kind,
          },
          {
            id: "state",
            header: t("admin.users.billing.col.state"),
            cell: (row) => row.state,
          },
          {
            id: "stars",
            header: t("admin.users.billing.col.stars"),
            cell: (row) => String(row.amount_stars),
          },
          {
            id: "created",
            header: t("admin.users.billing.col.created"),
            cell: (row) => formatDateTime(row.created_at),
          },
        ]}
      />
      <Table
        caption={t("admin.users.billing.refundsCaption")}
        rows={billing.refunds}
        rowKey={(row) => row.id}
        empty={
          <p className="text-body text-fg-muted">
            {t("admin.users.billing.noRefunds")}
          </p>
        }
        columns={[
          {
            id: "order",
            header: t("admin.users.billing.col.order"),
            cell: (row) => (
              <span className="font-mono text-footnote">{row.order_id}</span>
            ),
          },
          {
            id: "created",
            header: t("admin.users.billing.col.created"),
            cell: (row) => formatDateTime(row.created_at),
          },
        ]}
      />
    </div>
  );
}

/** Billing facts, or an explicit note when the session lacks the permission. */
export function BillingSection({
  billing,
  redacted,
}: {
  billing: UserBilling | undefined;
  redacted: boolean;
}) {
  const t = useT();
  return (
    <Card title={t("admin.users.billing.title")}>
      {redacted || billing === undefined ? (
        <Banner tone="info">{t("admin.users.billing.redacted")}</Banner>
      ) : (
        <BillingBody billing={billing} />
      )}
    </Card>
  );
}
